// MIRROR of server/src/modules/projectSurveys/surveyRecord.ts for the engineer PWA, which cannot import server code.
// Bound by src/lib/surveyRecord.parity.test.ts: edit the server file, then copy it here unchanged below this header.

// Session PJ2: what a project site survey RECORDS, and how two copies of it are merged.
//
// A survey's record is a flat map of CELLS, one per thing the engineer or the office can set, keyed by a PATH:
//
//   f.<fieldKey>                       a field in a section
//   t.<fieldKey>.<rowKey>.<colKey>     one cell of a table field (one row per floor or level)
//   tr.<fieldKey>.<rowKey>             a whole table row marked "could not access"
//   s.<sectionKey>                     a whole section marked "could not access"
//   n.<sectionKey>                     the notes box of a section
//   n._survey                          the notes box of the whole survey
//
// Row keys: "f0" (the lowest floor) up to "f199", plus "bottom" and "top" on a table taken at every level.
//
// WHY FLAT. The Director's rule (27 October 2026) is that a survey started on one phone and continued on another,
// or corrected by the office meanwhile, keeps the NEWER value of every field, never a whole-record overwrite of
// newer data by older. A flat map makes that one rule: for each path, the cell with the later `at` wins.
//
// ONE HOME. The engineer PWA mirrors this file in src/lib/surveyRecord.ts, bound by
// src/lib/surveyRecord.parity.test.ts. Pure: no I/O.

export const NO_ACCESS_REASONS = {
  locked: "Locked",
  unsafe: "Unsafe",
  occupied: "Occupied",
  not_built: "Not built yet",
  other: "Other",
} as const;
export type NoAccessReason = keyof typeof NO_ACCESS_REASONS;
export const NO_ACCESS_REASON_CODES = Object.keys(NO_ACCESS_REASONS) as NoAccessReason[];

export const NO_ACCESS_NOTE_MAX = 300;
export const SURVEY_TEXT_MAX = 4000;
export const SURVEY_LIST_MAX = 40;
export const SURVEY_ROW_MAX = 200;
// How far ahead of the server's clock a phone's clock may run before its stamp is pulled back to the server's.
// Without it a phone set a year ahead would win every merge for a year.
export const SURVEY_CLOCK_SKEW_MS = 10 * 60 * 1000;

export type SurveyValue = string | number | boolean | string[];

export interface SurveyCell {
  /** The value. null with `na` = could not access; null without `na` = cleared. */
  v: SurveyValue | null;
  /** ISO instant of the change: the device's clock (pulled back to the server's if ahead), or the server's for an office edit. */
  at: string;
  by: "engineer" | "office";
  /** Could not access, with the reason. */
  na?: { reason: NoAccessReason; note?: string };
  /** Where the value came from: typed, or read from a plate photo and confirmed by the office. */
  src?: "typed" | "photo_confirmed";
  /** The user who made the change (server-stamped; never trusted from a device). */
  uid?: string;
}

export interface SurveyRecordData {
  cells: Record<string, SurveyCell>;
}

export function emptyRecord(): SurveyRecordData {
  return { cells: {} };
}

const KEY = "[a-z][a-z0-9_]{0,59}";
const ROW = "(?:f(?:1[0-9]{2}|[1-9]?[0-9])|bottom|top)";
export const PATH_RE = new RegExp(
  `^(?:f\\.${KEY}|t\\.${KEY}\\.${ROW}\\.${KEY}|tr\\.${KEY}\\.${ROW}|s\\.${KEY}|n\\.(?:${KEY}|_survey))$`,
);

export function isPath(p: string): boolean {
  return PATH_RE.test(p);
}

export type ParsedPath =
  | { kind: "field"; field: string }
  | { kind: "cell"; field: string; row: string; col: string }
  | { kind: "row"; field: string; row: string }
  | { kind: "section"; section: string }
  | { kind: "notes"; section: string };

export function parsePath(p: string): ParsedPath | null {
  if (!isPath(p)) return null;
  const parts = p.split(".");
  switch (parts[0]) {
    case "f":
      return { kind: "field", field: parts[1] };
    case "t":
      return { kind: "cell", field: parts[1], row: parts[2], col: parts[3] };
    case "tr":
      return { kind: "row", field: parts[1], row: parts[2] };
    case "s":
      return { kind: "section", section: parts[1] };
    case "n":
      return { kind: "notes", section: parts[1] };
    default:
      return null;
  }
}

export const fieldPath = (field: string) => `f.${field}`;
export const cellPath = (field: string, row: string, col: string) => `t.${field}.${row}.${col}`;
export const rowPath = (field: string, row: string) => `tr.${field}.${row}`;
export const sectionPath = (section: string) => `s.${section}`;
export const notesPath = (section: string) => `n.${section}`;
export const SURVEY_NOTES_PATH = "n._survey";

/** Floor row keys, lowest first. */
export function floorRowKeys(count: number): string[] {
  const n = Math.max(0, Math.min(SURVEY_ROW_MAX - 2, Math.floor(count)));
  return Array.from({ length: n }, (_, i) => `f${i}`);
}

/** Level row keys: the pit bottom, every floor, the top of the shaft. */
export function levelRowKeys(count: number): string[] {
  return ["bottom", ...floorRowKeys(count), "top"];
}

function ms(at: string): number {
  const t = Date.parse(at);
  return Number.isFinite(t) ? t : 0;
}

/** True when `incoming` should replace `existing`: strictly newer, or nothing there yet. A tie keeps what is there. */
export function cellWins(incoming: SurveyCell, existing: SurveyCell | undefined): boolean {
  if (!existing) return true;
  return ms(incoming.at) > ms(existing.at);
}

/**
 * Merge `incoming` cells into `base`, per path, the newer winning. Returns a new record and the paths that changed.
 * Neither input is modified.
 */
export function mergeCells(
  base: SurveyRecordData,
  incoming: Record<string, SurveyCell>,
): { record: SurveyRecordData; changed: string[] } {
  const cells: Record<string, SurveyCell> = { ...base.cells };
  const changed: string[] = [];
  for (const [path, cell] of Object.entries(incoming)) {
    if (cellWins(cell, cells[path])) {
      cells[path] = cell;
      changed.push(path);
    }
  }
  return { record: { cells }, changed };
}

/** Read a record from a stored Json column, tolerating null and anything malformed (it reads as empty). */
export function readRecord(raw: unknown): SurveyRecordData {
  if (!raw || typeof raw !== "object") return emptyRecord();
  const cells = (raw as { cells?: unknown }).cells;
  if (!cells || typeof cells !== "object" || Array.isArray(cells)) return emptyRecord();
  const out: Record<string, SurveyCell> = {};
  for (const [path, c] of Object.entries(cells as Record<string, unknown>)) {
    if (!isPath(path) || !c || typeof c !== "object") continue;
    const cell = c as SurveyCell;
    if (typeof cell.at !== "string" || (cell.by !== "engineer" && cell.by !== "office")) continue;
    out[path] = cell;
  }
  return { cells: out };
}

/** The value at a path, or undefined when blank, cleared or marked could not access. */
export function valueAt(record: SurveyRecordData, path: string): SurveyValue | undefined {
  const c = record.cells[path];
  if (!c || c.na || c.v === null || c.v === "" || (Array.isArray(c.v) && c.v.length === 0)) return undefined;
  return c.v;
}

export function noAccessAt(record: SurveyRecordData, path: string): SurveyCell["na"] | undefined {
  return record.cells[path]?.na;
}

/** A number at a path, or undefined. Accepts a stored number or a numeric string. */
export function numberAt(record: SurveyRecordData, path: string): number | undefined {
  const v = valueAt(record, path);
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && /^-?\d+(?:\.\d+)?$/.test(v.trim())) return Number(v.trim());
  return undefined;
}

/** Pull a device stamp back to `now` when it is further ahead than the allowed skew. */
export function clampStamp(at: string, now: Date): string {
  const t = ms(at);
  if (t === 0) return now.toISOString();
  return t > now.getTime() + SURVEY_CLOCK_SKEW_MS ? now.toISOString() : new Date(t).toISOString();
}

/** True when the record holds anything at all (a value, a could-not-access mark or a note). */
export function recordTouched(record: SurveyRecordData): boolean {
  return Object.values(record.cells).some((c) => c.na != null || (c.v !== null && c.v !== "" && !(Array.isArray(c.v) && c.v.length === 0)));
}
